import React from "react";
import { any, func } from "prop-types";
import { connect } from "react-redux";
import moment from "moment";
import history from "../../history";
import FormCreatorV2 from "../../common/FormCreatorV2";
import {
  formCreatorConvertToValues,
  getField,
  useForm,
} from "../../common/FormCreator/helpers";
import {
  submitDavinFoodsForm,
  postDavinFoodsForm,
} from "../../redux/actions/index";

const InductionChecklist = ({ appData, submitForm, postForm }) => {
  const fullName = `${appData.firstName} ${appData.lastName}`;
  const typeCode = "YDF7";
  const formName = "inductionChecklist";
  const onboarding = appData.davinFoodsOnboarding;

  let initialData = {
    name: fullName,
    signature: fullName,
    date: moment().format("DD/MM/YYYY"),
  };

  if (appData.davinFoodsOnboarding.inductionChecklist) {
    initialData = {
      ...initialData,
      ...appData.davinFoodsOnboarding.inductionChecklist,
    };
  }
  const foodSafetySubjects = [
    { id: "safety", title: "Food safety legislation" },
    {
      id: "hazard",
      title: "Hazard Analysis and Critical Control Point (HACCP)",
    },
    {
      id: "contaminant",
      title:
        "Food contaminants (Physical, Chemical, Microbiological and Allergenic)",
    },
    { id: "hygiene", title: "Personal hygiene rules" },
    { id: "breakage", title: "Glass breakage procedure" },
    { id: "knife", title: "Knife control procedure" },
    { id: "microbiological", title: "Microbiological growth" },
    { id: "poisoning", title: "Food poisoning" },
    { id: "allergens", title: "Allergens and specific handling requirements" },
    { id: "handwashing", title: "Handwashing" },
    { id: "risk", title: "Low risk and high care area segregation" },
    { id: "pest", title: "Pest Control" },
    { id: "security", title: "Site security" },
    { id: "reporting", title: "Confidential Reporting (Navex Global)" },
    { id: "slavery", title: "Modern Slavery" },
    { id: "policy", title: "Food Safety and Quality Policy" },
    { id: "procedure", title: "Changing Procedure" },
    { id: "foodPPE", title: "Provision of PPE" },
  ];

  const healthSafetySubjects = [
    {
      id: "health",
      title: "Health and Safety at Work Act and other relevant legislation",
    },
    { id: "guide", title: "Davin Foods 10 point safety guide" },
    { id: "measures", title: "Site risks and control measures" },
    { id: "assessment", title: "Principles of risk assessment" },
    { id: "arrangements", title: "Site safety arrangements" },
    { id: "fire", title: "Fire procedures" },
    { id: "manual", title: "Principles of Manual Handling" },
    { id: "chemical", title: "Chemical Safety - COSHH" },
    { id: "colour", title: "Cleaning Equipment Colour Coding" },
    { id: "aid", title: "First aid arrangements" },
    { id: "accident", title: "Accident/incident reporting" },
    { id: "healthPPE", title: "Provision of PPE" },
  ];

  const getVal = getField(initialData);

  const initialState = {
    blurb1: {
      htmlObj: (
        <>
          <p className="text-center">
            The below set of induction training packages are included as part of
            the onboarding process and <strong>must</strong> be completed prior
            to you starting your role in Davin Foods. Existing staff will also
            complete these as part of refresher training.
          </p>
          <ul>
            <li>PR901 Welcome to Davin Foods</li>
            <li>PR902 Food Safety Induction</li>
            <li>PR903 Managing Food Allergens at Davin Foods</li>
            <li>PR904 Health and Safety Overview</li>
            <li>PR905 Introduction to Chemicals and Safe Cleaning Practises</li>
            <li>PR906 Fire Controls and Evacuation</li>
            <li>PR907 Manual Handling</li>
          </ul>
          <p className="text-center">
            Once you have completed each Induction Training package it is
            important that you complete the quiz at the end.
          </p>
          <div className="text-center">
            <p className="font-weight-bold">Food Safety Induction Training</p>
            <p>
              The subjects listed below are included in the (PR902) Food Safety
              Induction and (PR903) Managing Food Allergens at Davin Foods
              training packages and must be completed by all new starters,
              regardless of their role.
              <br />
              Job specific training is provided separately as required for the
              employee to safely undertake their role within the Company.
            </p>
          </div>
          <p className="mb-2 text-center">
            Tick each of the subjects below, to acknowledge that this subject
            was clearly understood through the training package
          </p>
        </>
      ),
    },
    ...foodSafetySubjects.reduce(
      (obj, checkbox) => ({
        ...obj,
        [checkbox.id]: {
          title: checkbox.title,
          value: getVal(checkbox.id),
          type: "singlecheckbox",
          mandatory: true,
        },
      }),
      {}
    ),
    blurb2: {
      htmlObj: (
        <div className="text-center my-2">
          <p className="font-weight-bold">
            Health and Safety Induction Training
          </p>
          <p>
            The subjects listed below are included in the (PR904) H&S Safety
            Overview, (PR905) Introduction to Chemicals and Safe Cleaning
            Practises, (PR906) Fire controls and Evacuation and (PR907) Manual
            Handling training packages and must be completed by all new
            starters, regardless of their role. Job specific training is
            provided separately as required for the employee to safely undertake
            their role within the Company.
          </p>
          <p>
            Tick each of the subjects below, to acknowledge that this subject
            was clearly understood through the training package
          </p>
        </div>
      ),
    },
    ...healthSafetySubjects.reduce(
      (obj, checkbox) => ({
        ...obj,
        [checkbox.id]: {
          title: checkbox.title,
          value: getVal(checkbox.id),
          type: "singlecheckbox",
          mandatory: true,
        },
      }),
      {}
    ),
    blurb3: {
      htmlObj: (
        <p className="text-center my-2 font-weight-bold">
          By signing this document I acknowledge that I have watched each
          training package as listed in this document (PR901 to PR907) and have
          a clear understanding. I also confirm that by signing this document I
          agree to comply with the company’s Health and Safety and Food Safety
          arrangements and understand that failure to do so could lead to
          disciplinary action.
        </p>
      ),
    },
    name: {
      key: "name",
      title: "Full Name",
      value: getVal("name"),
      mandatory: true,
      disabled: true,
    },
    signature: {
      key: "signature",
      title: "Signature",
      value: getVal("signature"),
      mandatory: true,
      disabled: true,
    },
    date: {
      key: "date",
      title: "Date of Induction/Refresher",
      value: getVal("date"),
      mandatory: true,
      disabled: true,
    },
  };
  const [fields, setField] = useForm(initialState);
  const submitted = onboarding[typeCode];
  const redirectBack = () =>
    history.push("/profile/edit/davin-foods-onboarding");
  const handleSubmit = () => {
    const fieldValuesPayload = formCreatorConvertToValues(fields);
    submitForm(
      formName,
      {
        ...fieldValuesPayload,
      },
      typeCode
    );
    redirectBack();
  };

  const handleSave = () => {
    const fieldValuesPayload = formCreatorConvertToValues(fields);
    postForm(formName, {
      ...fieldValuesPayload,
    });
    redirectBack();
  };

  return (
    <>
      {submitted ? (
        redirectBack()
      ) : (
        <FormCreatorV2
          formTitle={"Davin Foods Induction Checklist"}
          helper={""}
          fields={fields}
          setField={setField}
          handleCancel={() => redirectBack()}
          onboardingBranch={"davinFoods"}
          submitSingleForm={true}
          handleSingleSubmit={handleSubmit}
          handleSubmit={handleSave}
        />
      )}
    </>
  );
};
InductionChecklist.propTypes = {
  appData: any,
  submitForm: func,
  postForm: func,
};
const mapDispatchToProps = {
  submitForm: submitDavinFoodsForm,
  postForm: postDavinFoodsForm,
};
const mapStateToProps = (state) => ({
  appData: state.core.app,
});
const withRedux = connect(mapStateToProps, mapDispatchToProps);
export default withRedux(InductionChecklist);
